import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import { EmptyState, ErrorBox, SkeletonRows } from "../components/States";
import { fmtMoney } from "../format";
import { CounterpartyCard } from "./CounterpartyCard";
import { MeResponse } from "../types";

interface SearchDeal {
  id: number;
  deal_type: string;
  counterparty: string | null;
  company: string | null;
  bank: string | null;
  amount: number;
  currency: string;
  status: string;
}
interface SearchCp {
  id: number;
  name: string;
  kind: string | null;
  status: string | null;
}
interface SearchResult {
  deals: SearchDeal[];
  counterparties: SearchCp[];
}

export function SearchPage({ me }: { me: MeResponse }) {
  const [q, setQ] = useState("");
  const [result, setResult] = useState<SearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openCp, setOpenCp] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const needle = q.trim();
    if (needle.length < 1) {
      setResult(null);
      setError(null);
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get<SearchResult>(
          `/api/search?q=${encodeURIComponent(needle)}`,
        );
        setResult(r);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [q]);

  const empty =
    result !== null &&
    result.deals.length === 0 &&
    result.counterparties.length === 0;

  if (openCp != null) {
    return (
      <CounterpartyCard
        cpId={openCp}
        me={me}
        onClose={() => setOpenCp(null)}
      />
    );
  }

  return (
    <div className="space-y-3">
      <h1 className="text-xl font-semibold">🔍 Поиск</h1>
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={
          me.permissions.key_analytic_only
            ? "№ поручения, код (CP-/CO-/D-…) или сумма…"
            : "№ заявки, контрагент или сумма…"
        }
        className="w-full px-3 py-2.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
      />
      <div className="text-xs text-tg-hint">
        {me.permissions.key_analytic_only
          ? "Поиск по коду сущности (CP-NNNNNN / CO-/BK-/OP-/SC-/D-), номеру поручения или сумме."
          : "Единый поиск по заявкам и контрагентам — без переключения вкладок."}
      </div>

      {error && <ErrorBox text={error} />}
      {loading && <SkeletonRows rows={4} />}

      {empty && !loading && (
        <EmptyState icon="🔍" text="Ничего не найдено." />
      )}

      {result && result.counterparties.length > 0 && (
        <section>
          <div className="text-[11px] uppercase tracking-wider text-tg-sectionHeader mb-1">
            Контрагенты
          </div>
          <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
            {result.counterparties.map((c) => (
              <button
                key={c.id}
                onClick={() => setOpenCp(c.id)}
                className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 active:bg-tg-secondaryBg/50"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-xs text-tg-hint">
                    #{c.id}
                    {c.kind ? ` · ${c.kind}` : ""}
                    {c.status ? ` · ${c.status}` : ""}
                  </div>
                </div>
                <span className="text-tg-hint">›</span>
              </button>
            ))}
          </div>
        </section>
      )}

      {result && result.deals.length > 0 && (
        <section>
          <div className="text-[11px] uppercase tracking-wider text-tg-sectionHeader mb-1">
            Заявки
          </div>
          <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
            {result.deals.map((d) => (
              <div key={d.id} className="px-4 py-2.5 text-sm">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium">
                    №{d.id} · {d.counterparty || "—"}
                  </span>
                  <span className="text-xs text-tg-hint whitespace-nowrap">
                    {d.deal_type}
                  </span>
                </div>
                <div className="mt-0.5">
                  <span className="font-semibold tabular-nums">
                    {fmtMoney(d.amount)} {d.currency}
                  </span>
                  <span className="text-tg-hint text-xs">
                    {d.company ? ` · ${d.company}` : ""}
                    {d.bank ? ` · ${d.bank}` : ""}
                  </span>
                </div>
                <div className="text-xs text-tg-hint mt-0.5">
                  Статус: {d.status}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
